import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { USER_PREFERENCE_VALUE_TYPE, UserPreference } from '@/api/model';
import { isEmpty } from '@/shared/util';

type PreferencesFormValues = Record<string, UserPreference['currentValue']>;

function toFieldKey(key: string): string {
  return key.replaceAll('.', '__');
}

function fromFieldKey(fieldKey: string): string {
  return fieldKey.replaceAll('__', '.');
}

export function usePreferencesModal(open: boolean, onOpenChange: (open: boolean) => void) {
  const queryClient = useQueryClient();
  const patchMutation = api.fct.userPreference.mutation.usePatchUserPreferences(queryClient);
  const resetMutation = api.fct.userPreference.mutation.useResetUserPreferences();

  const { data: preferences, isLoading } = api.fct.userPreference.query.useGetUserPreferences({
    enabled: open,
    queryFn: () => api.fct.userPreference.request.getUserPreferences(),
  });

  const safePreferences = useMemo(
    () => (Array.isArray(preferences) ? preferences : []),
    [preferences],
  );

  const defaultValues = useMemo(
    () =>
      Object.fromEntries(
        safePreferences.map((pref) => [toFieldKey(pref.key), pref.currentValue]),
      ) as PreferencesFormValues,
    [safePreferences],
  );

  const { control, formState, reset, setValue } = useForm<PreferencesFormValues>({
    defaultValues,
  });

  const watchedValues = useWatch({ control }) as PreferencesFormValues;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const editedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(watchedValues).map(([k, v]) => [fromFieldKey(k), v]),
      ) as PreferencesFormValues,
    [watchedValues],
  );

  const sortedPreferences = useMemo(() => {
    if (isEmpty(safePreferences)) {
      return [];
    }

    return [...safePreferences].sort(
      (a, b) => (a.uiConfig?.sortOrder ?? 0) - (b.uiConfig?.sortOrder ?? 0),
    );
  }, [safePreferences]);

  const handlePreferenceChange = (key: string, value: string) => {
    const preference = safePreferences.find((entry) => entry.key === key);

    if (!preference) {
      return;
    }

    const fieldKey = toFieldKey(key);

    if (preference.valueType === USER_PREFERENCE_VALUE_TYPE.BOOLEAN) {
      setValue(fieldKey, value === 'true', { shouldDirty: true });

      return;
    }

    setValue(fieldKey, value, { shouldDirty: true });
  };

  const handleCancel = () => {
    reset(defaultValues);
    onOpenChange(false);
  };

  const handleReset = () => {
    resetMutation.mutate(undefined, {
      onSuccess: (preferences) => {
        const values = Object.fromEntries(
          preferences.map((pref) => [toFieldKey(pref.key), pref.currentValue]),
        ) as PreferencesFormValues;

        reset(values, { keepDefaultValues: true });
      },
    });
  };

  const handleSave = () => {
    if (isEmpty(safePreferences)) {
      return;
    }

    const changedPreferences = safePreferences
      .filter((pref) => editedValues[pref.key] !== pref.currentValue)
      .map((pref) => ({ key: pref.key, value: editedValues[pref.key] }));

    if (isEmpty(changedPreferences)) {
      return;
    }

    patchMutation.mutate(
      { payload: { preferences: changedPreferences } },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return {
    editedValues,
    handleCancel,
    handlePreferenceChange,
    handleReset,
    handleSave,
    isDirty: formState.isDirty,
    isLoading,
    isPatching: patchMutation.isPending,
    isResetting: resetMutation.isPending,
    sortedPreferences,
  };
}
